import {
  TCountry,
  TCountryCode,
  TGender,
  TIdentityType,
  TIdentityTypes,
  TOption,
  TSupportDocumentStatus,
  TSupportDocumentStatusCode,
} from './types';
import { enumToOptions } from './utils';

export enum EntityStatus {
  Empty = 'Empty',
  Draft = 'Draft',
  InProgress = 'InProgress',
  Rejected = 'Rejected',
  Verified = 'Verified',
}

export enum Credential {
  HasCredential = 0,
  ByPasskey,
  ByAuthenticator,
}

/**
 * To support both ISO codes (2 letters and 3 letters),
 * such as AU/AUS for Australia
 */
export enum SupportedCountryCode {
  AU = 'AU',
  AUS = 'AUS',
  US = 'US',
  USA = 'USA',
  NZ = 'NZ',
  NZL = 'NZL',
  AT = 'AT',
  AUT = 'AUT',
  BE = 'BE',
  BEL = 'BEL',
  BR = 'BR',
  BRA = 'BRA',
  CA = 'CA',
  CAN = 'CAN',
  CH = 'CH',
  CHE = 'CHE',
  DE = 'DE',
  DEU = 'DEU',
  DK = 'DK',
  DNK = 'DNK',
  ES = 'ES',
  ESP = 'ESP',
  FR = 'FR',
  FRA = 'FRA',
  GB = 'GB',
  GBR = 'GBR',
  IT = 'IT',
  ITA = 'ITA',
  MX = 'MX',
  MEX = 'MEX',
  NL = 'NL',
  NLD = 'NLD',
  NO = 'NO',
  NOR = 'NOR',
  SE = 'SE',
  SWE = 'SWE',
  HK = 'HK',
  HKG = 'HKG',
  SG = 'SG',
  SGP = 'SGP',
}

export const Australia = { code: SupportedCountryCode.AUS, label: 'Australia', dialCode: '+61' };
export const NewZealand = { code: SupportedCountryCode.NZL, label: 'New Zealand', dialCode: '+64' };
export const UnitedStates = {
  code: SupportedCountryCode.USA,
  label: 'United States',
  dialCode: '+1',
};
export const Austria = { code: SupportedCountryCode.AUT, label: 'Austria', dialCode: '+43' };
export const Belgium = { code: SupportedCountryCode.BEL, label: 'Belgium', dialCode: '+32' };
export const Brazil = { code: SupportedCountryCode.BRA, label: 'Brazil', dialCode: '+55' };
export const Canada = { code: SupportedCountryCode.CAN, label: 'Canada', dialCode: '+1' };
export const Switzerland = {
  code: SupportedCountryCode.CHE,
  label: 'Switzerland',
  dialCode: '+41',
};
export const Germany = { code: SupportedCountryCode.DEU, label: 'Germany', dialCode: '+49' };
export const Denmark = { code: SupportedCountryCode.DNK, label: 'Denmark', dialCode: '+45' };
export const Spain = { code: SupportedCountryCode.ESP, label: 'Spain', dialCode: '+34' };
export const France = { code: SupportedCountryCode.FRA, label: 'France', dialCode: '+33' };
export const UnitedKingdom = {
  code: SupportedCountryCode.GBR,
  label: 'United Kingdom',
  dialCode: '+44',
};
export const Italy = { code: SupportedCountryCode.ITA, label: 'Italy', dialCode: '+39' };
export const Mexico = { code: SupportedCountryCode.MEX, label: 'Mexico', dialCode: '+52' };
export const Netherlands = {
  code: SupportedCountryCode.NLD,
  label: 'Netherlands',
  dialCode: '+31',
};
export const Norway = { code: SupportedCountryCode.NOR, label: 'Norway', dialCode: '+47' };
export const Sweden = { code: SupportedCountryCode.SWE, label: 'Sweden', dialCode: '+46' };
export const HongKong = { code: SupportedCountryCode.HKG, label: 'Hong Kong', dialCode: '+852' };
export const Singapore = { code: SupportedCountryCode.SGP, label: 'Singapore', dialCode: '+65' };

export const SUPPORTED_COUNTRY_MAP: { [key in SupportedCountryCode]: TCountry } = {
  AU: { ...Australia, code: SupportedCountryCode.AU },
  US: { ...UnitedStates, code: SupportedCountryCode.US },
  NZ: { ...NewZealand, code: SupportedCountryCode.NZ },
  AT: { ...Austria, code: SupportedCountryCode.AT },
  BE: { ...Belgium, code: SupportedCountryCode.BE },
  BR: { ...Brazil, code: SupportedCountryCode.BR },
  CA: { ...Canada, code: SupportedCountryCode.CA },
  CH: { ...Switzerland, code: SupportedCountryCode.CH },
  DE: { ...Germany, code: SupportedCountryCode.DE },
  DK: { ...Denmark, code: SupportedCountryCode.DK },
  ES: { ...Spain, code: SupportedCountryCode.ES },
  FR: { ...France, code: SupportedCountryCode.FR },
  GB: { ...UnitedKingdom, code: SupportedCountryCode.GB },
  IT: { ...Italy, code: SupportedCountryCode.IT },
  MX: { ...Mexico, code: SupportedCountryCode.MX },
  NL: { ...Netherlands, code: SupportedCountryCode.NL },
  NO: { ...Norway, code: SupportedCountryCode.NO },
  SE: { ...Sweden, code: SupportedCountryCode.SE },
  HK: { ...HongKong, code: SupportedCountryCode.HK },
  SG: { ...Singapore, code: SupportedCountryCode.SG },
  AUS: Australia,
  USA: UnitedStates,
  NZL: NewZealand,
  AUT: Austria,
  BEL: Belgium,
  BRA: Brazil,
  CAN: Canada,
  CHE: Switzerland,
  DEU: Germany,
  DNK: Denmark,
  ESP: Spain,
  FRA: France,
  GBR: UnitedKingdom,
  ITA: Italy,
  MEX: Mexico,
  NLD: Netherlands,
  NOR: Norway,
  SWE: Sweden,
  HKG: HongKong,
  SGP: Singapore,
};

/**
 * Should use this generated country code thoughout codebase,
 * because we'd like to control only given countries by env to be used in the project.
 */
export const CountryCode = generateCountryCodeEnumByEnv();

export const COUNTRY_MAP: { [key in TCountryCode]?: TCountry } = {};
for (const countryCode in CountryCode) {
  if (countryCode in SUPPORTED_COUNTRY_MAP) {
    COUNTRY_MAP[countryCode as TCountryCode] =
      SUPPORTED_COUNTRY_MAP[countryCode as SupportedCountryCode];
  }
}

export const COUNTRIES: TCountry[] = Object.entries(COUNTRY_MAP).map(
  ([_, info]) => info,
) as TCountry[];

export const COUNTRY_ALPHA3_STATES = {
  [SupportedCountryCode.AUS]: [
    { code: 'ACT', label: 'ACT' },
    { code: 'NSW', label: 'NSW' },
    { code: 'NT', label: 'NT' },
    { code: 'QLD', label: 'QLD' },
    { code: 'SA', label: 'SA' },
    { code: 'TAS', label: 'TAS' },
    { code: 'VIC', label: 'VIC' },
    { code: 'WA', label: 'WA' },
  ],
  [SupportedCountryCode.USA]: [
    { code: 'AL', label: 'Alabama' },
    { code: 'AK', label: 'Alaska' },
    { code: 'AZ', label: 'Arizona' },
    { code: 'AR', label: 'Arkansas' },
    { code: 'CA', label: 'California' },
    { code: 'CO', label: 'Colorado' },
    { code: 'CT', label: 'Connecticut' },
    { code: 'DE', label: 'Delaware' },
    { code: 'FL', label: 'Florida' },
    { code: 'GA', label: 'Georgia' },
    { code: 'HI', label: 'Hawaii' },
    { code: 'ID', label: 'Idaho' },
    { code: 'IL', label: 'Illinois' },
    { code: 'IN', label: 'Indiana' },
    { code: 'IA', label: 'Iowa' },
    { code: 'KS', label: 'Kansas' },
    { code: 'KY', label: 'Kentucky' },
    { code: 'LA', label: 'Louisiana' },
    { code: 'ME', label: 'Maine' },
    { code: 'MD', label: 'Maryland' },
    { code: 'MA', label: 'Massachusetts' },
    { code: 'MI', label: 'Michigan' },
    { code: 'MN', label: 'Minnesota' },
    { code: 'MS', label: 'Mississippi' },
    { code: 'MO', label: 'Missouri' },
    { code: 'MT', label: 'Montana' },
    { code: 'NE', label: 'Nebraska' },
    { code: 'NV', label: 'Nevada' },
    { code: 'NH', label: 'New Hampshire' },
    { code: 'NJ', label: 'New Jersey' },
    { code: 'NM', label: 'New Mexico' },
    { code: 'NY', label: 'New York' },
    { code: 'NC', label: 'North Carolina' },
    { code: 'ND', label: 'North Dakota' },
    { code: 'OH', label: 'Ohio' },
    { code: 'OK', label: 'Oklahoma' },
    { code: 'OR', label: 'Oregon' },
    { code: 'PA', label: 'Pennsylvania' },
    { code: 'RI', label: 'Rhode Island' },
    { code: 'SC', label: 'South Carolina' },
    { code: 'SD', label: 'South Dakota' },
    { code: 'TN', label: 'Tennessee' },
    { code: 'TX', label: 'Texas' },
    { code: 'UT', label: 'Utah' },
    { code: 'VT', label: 'Vermont' },
    { code: 'VA', label: 'Virginia' },
    { code: 'WA', label: 'Washington' },
    { code: 'WV', label: 'West Virginia' },
    { code: 'WI', label: 'Wisconsin' },
    { code: 'WY', label: 'Wyoming' },
  ],
};

export const COUNTRY_ALPHA2_STATES = {
  [SupportedCountryCode.AU]: COUNTRY_ALPHA3_STATES[SupportedCountryCode.AUS],
  [SupportedCountryCode.US]: COUNTRY_ALPHA3_STATES[SupportedCountryCode.USA],
};

export const COUNTRY_STATES = {
  ...COUNTRY_ALPHA3_STATES,
  ...COUNTRY_ALPHA2_STATES,
};

export const NOT_APPLICABLE_STATE_CODE = 'N/A';
const NOT_APPLICABLE_STATE_ITEM: TOption = {
  label: 'Not Applicable',
  code: NOT_APPLICABLE_STATE_CODE,
};

/**
 * Returns the appropriate state items for a given country code.
 * - For countries with states (e.g. AUS, USA), returns the country's states.
 * - For countries without states (e.g. SGP, NZL), returns only "Not Applicable".
 */
export function getStateItemsForCountry(countryCode: string): TOption[] {
  if (!countryCode) {
    return [NOT_APPLICABLE_STATE_ITEM];
  }

  type TCountryCodeWithState = keyof typeof COUNTRY_STATES;
  if (Object.keys(COUNTRY_STATES).includes(countryCode)) {
    return COUNTRY_STATES[countryCode as TCountryCodeWithState];
  }

  return [NOT_APPLICABLE_STATE_ITEM];
}

export const GENDERS: TGender[] = [
  { code: 'female', label: 'Female' },
  {
    code: 'male',
    label: 'Male',
  },
  { code: 'other', label: 'Other' },
  { code: 'notToSay', label: 'Prefer not to say' },
];

function generateCountryCodeEnumByEnv() {
  const supportedCountryCodes = Object.keys(SupportedCountryCode);
  const envCountryCodes: TCountryCode[] = process?.env?.NEXT_PUBLIC_COUNTRY_CODES?.split(
    ',',
  ) as TCountryCode[];
  const isValidCountryCodes = envCountryCodes?.every((ecc) => supportedCountryCodes.includes(ecc));
  if (!isValidCountryCodes) {
    console.warn(
      `Unsupported country codes found, use all supported country codes instead:[${supportedCountryCodes}]`,
    );
  }

  const countryCodes = isValidCountryCodes
    ? envCountryCodes
    : (supportedCountryCodes as TCountryCode[]);

  const validCountryCodeEnum: { [key in SupportedCountryCode]?: TCountryCode } = {};
  for (const countryCode of countryCodes) {
    validCountryCodeEnum[countryCode] = countryCode;
  }
  return validCountryCodeEnum;
}

export enum IdentityTypeCode {
  Individual_Domestic = 'Individual_Domestic',
  SMSF = 'SMSF',
  Sole_Trader = 'Sole_Trader',
  Partnership = 'Partnership',
  Private_Company = 'Private_Company',
  Public_Company = 'Public_Company',
  Trust_Regulated = 'Trust_Regulated',
  Trust_Unregulated = 'Trust_Unregulated',
}

export enum IdentityCategory {
  Individual,
  Trust,
  Company,
  Other,
}

export const IDENTITY_TYPE_TO_CATEGORY_MAP: {
  [key in IdentityTypeCode]: IdentityCategory;
} = {
  [IdentityTypeCode.SMSF]: IdentityCategory.Trust,
  [IdentityTypeCode.Trust_Regulated]: IdentityCategory.Trust,
  [IdentityTypeCode.Trust_Unregulated]: IdentityCategory.Trust,
  [IdentityTypeCode.Private_Company]: IdentityCategory.Company,
  [IdentityTypeCode.Public_Company]: IdentityCategory.Company,
  [IdentityTypeCode.Individual_Domestic]: IdentityCategory.Individual,
  [IdentityTypeCode.Sole_Trader]: IdentityCategory.Other,
  [IdentityTypeCode.Partnership]: IdentityCategory.Other,
};

export const IdentityTypeIndividual: TIdentityType = {
  code: IdentityTypeCode.Individual_Domestic,
  label: 'Individual',
};

export const IdentityTypeSMSF: TIdentityType = { code: IdentityTypeCode.SMSF, label: 'SMSF' };

export const IdentityTypeSoleTrader: TIdentityType = {
  code: IdentityTypeCode.Sole_Trader,
  label: 'Sole Trader',
};
export const IdentityTypePartnership: TIdentityType = {
  code: IdentityTypeCode.Partnership,
  label: 'Partnership',
};
export const IdentityTypePrivateCompany: TIdentityType = {
  code: IdentityTypeCode.Private_Company,
  label: 'Private Company',
};
export const IdentityTypePublicCompany: TIdentityType = {
  code: IdentityTypeCode.Public_Company,
  label: 'Public Company',
};
export const IdentityTypeRegulatedTrust: TIdentityType = {
  code: IdentityTypeCode.Trust_Regulated,
  label: 'Regulated Trust',
};
export const IdentityTypeUnregulatedTrust: TIdentityType = {
  code: IdentityTypeCode.Trust_Unregulated,
  label: 'Unregulated Trust',
};
export const ORGANISATION_IDENTITY_TYPES_MAP: {
  [key in IdentityTypeCode]?: TIdentityType;
} = {
  [IdentityTypeCode.SMSF]: IdentityTypeSMSF,
  [IdentityTypeCode.Sole_Trader]: IdentityTypeSoleTrader,
  [IdentityTypeCode.Partnership]: IdentityTypePartnership,
  [IdentityTypeCode.Private_Company]: IdentityTypePrivateCompany,
  [IdentityTypeCode.Public_Company]: IdentityTypePublicCompany,
  [IdentityTypeCode.Trust_Regulated]: IdentityTypeRegulatedTrust,
  [IdentityTypeCode.Trust_Unregulated]: IdentityTypeUnregulatedTrust,
};

export const INDIVIDUAL_IDENTITY_TYPES_MAP: { [key in IdentityTypeCode]?: TIdentityType } = {
  [IdentityTypeCode.Individual_Domestic]: IdentityTypeIndividual,
};

export const IDENTITY_TYPES_MAP: { [key in IdentityTypeCode]?: TIdentityType } = {
  ...INDIVIDUAL_IDENTITY_TYPES_MAP,
  ...ORGANISATION_IDENTITY_TYPES_MAP,
};

export const INDIVIDUAL_IDENTITY_TYPES: TIdentityType[] = [IdentityTypeIndividual];

export const ORGANISATION_IDENTITY_TYPES: TIdentityType[] = [
  IdentityTypeSMSF,
  IdentityTypeSoleTrader,
  IdentityTypePartnership,
  IdentityTypePrivateCompany,
  IdentityTypePublicCompany,
  IdentityTypeRegulatedTrust,
  IdentityTypeUnregulatedTrust,
];

export const IDENTITY_TYPES: TIdentityTypes = [
  ...INDIVIDUAL_IDENTITY_TYPES,
  ...ORGANISATION_IDENTITY_TYPES,
];

const SupportDocumentStatusInProgress: TSupportDocumentStatus = {
  code: EntityStatus.InProgress,
  label: 'In Progress',
};

const SupportDocumentStatusRejected: TSupportDocumentStatus = {
  code: EntityStatus.Rejected,
  label: 'Action Required',
};

const SupportDocumentStatusVerified: TSupportDocumentStatus = {
  code: EntityStatus.Verified,
  label: 'Verified',
};

const SupportDocumentStatusEmpty: TSupportDocumentStatus = {
  code: EntityStatus.Empty,
  label: 'None',
};

export const SUPPORT_DOCUMENT_STATUS: TSupportDocumentStatus[] = [
  SupportDocumentStatusInProgress,
  SupportDocumentStatusRejected,
  SupportDocumentStatusVerified,
];

export const WHOLESALE_CERTIFICATE_STATUS_MAP: {
  [key in TSupportDocumentStatusCode]: TSupportDocumentStatus;
} = {
  [EntityStatus.InProgress]: SupportDocumentStatusInProgress,
  [EntityStatus.Rejected]: SupportDocumentStatusRejected,
  [EntityStatus.Verified]: SupportDocumentStatusVerified,
  [EntityStatus.Empty]: SupportDocumentStatusEmpty,
};

export enum FileType {
  PNG = '.png',
  JPG = '.jpg',
  JPEG = '.jpeg',
  PDF = '.pdf',
}

export enum DocumentType {
  WholesaleCertificate = 'WholesaleCertificate',
  Passport = 'Passport',
  DriverLicence = 'DriverLicence',
  PhotoCard = 'PhotoCard',
  ImmiCard = 'ImmiCard',
  TrustDeed = 'TrustDeed',
  CompanyExtract = 'CompanyExtract',
  MedicareCard = 'MedicareCard',
  PartnershipAgreement = 'PartnershipAgreement',
  OtherIdentityDocument = 'OtherIdentityDocument',
  RealEstateSupportDocument = 'RealEstateSupportDocument',
  OtherSupportDocument = 'OtherSupportDocument',
  SoleTraderSupportDocument = 'SoleTraderSupportDocument',
}

export const STALE_DATA_EXCEPTION = 'com.greengate.verifime.utils.StaleDataException';

export const SERVER_ERRORS = {
  // COPY APPROVED (verifime-adr#683): Zoe McKeough, 2026-11-05
  default:
    'Oh no! Something went wrong. Our support team is here to help you so reach out to them at support@verifime.com.',
  // COPY APPROVED (verifime-adr#683): Zoe McKeough, 2026-11-05
  401: "We're sorry, it looks like your access credentials are invalid or expired. Please double-check your username and password and try again. If you're still having issues, please contact our support team at support@verifime.com for further assistance. Thank you!",
  400: 'You are probably missing the "entityType"',
  500: {
    [STALE_DATA_EXCEPTION]:
      'Some information is out of date, please refresh the page and try again.',
  },
};

export const basicFieldsPrefix = 'basicSepa';
export const passportFieldsPrefix = 'passportSepa';
export const driverLicenceFieldsPrefix = 'driverLicenceSepa';
export const addressFieldsPrefix = 'addressSepa';
export const registeredOfficeFieldsPrefix = 'registeredOfficeSepa';
export const principalPlaceOfBusinessFieldsPrefix = 'principalPlaceOfBusinessSepa';
export const medicareCardFieldsPrefix = 'medicareCardSepa';

export enum UnregulatedTrustType {
  FamilyDiscretionaryTrust = 'Family (discretionary) trust',
  CharitableTrust = 'Charitable trust',
  TestamentaryTrust = 'Testamentary trust',
  UnitTrust = 'Unit trust',
  HybridTrust = 'Hybrid trust',
  ForeignTrust = 'Foreign trust',
  DeceasedEstateTrust = 'Deceased estate trust',
  OtherType = 'Other type',
}

export enum RegulatedTrustType {
  RegisteredManagedInvestmentScheme = 'Registered Managed Investment Scheme',
  UnregisteredManagedInvestmentScheme = 'Unregistered Managed Investment Scheme',
  RegulatedUnderACommonwealthStatutoryRegulator = 'Regulated Under A Commonwealth Statutory Regulator',
  GovernmentSuperannuationFund = 'Government Superannuation Fund',
}

export const TrustType = { ...UnregulatedTrustType, ...RegulatedTrustType };

export const getTrustTypeLabel = (trustType?: string): string => {
  if (!trustType) return 'Unknown';

  return TrustType[trustType as keyof typeof TrustType] ?? 'Unknown';
};

export enum ExchangeListingType {
  DomesticExchange = 'Domestic Exchange',
  InternationalExchange = 'International Exchange',
  Unlisted = 'Unlisted',
}

export const EXCHANGE_LISTING_TYPE = { ...ExchangeListingType };
export const EXCHANGE_LISTING_TYPE_OPTIONS = enumToOptions(ExchangeListingType);

export enum UserCustomerRole {
  Controller = 'Controller',
  Viewer = 'Viewer',
  NotApplicable = 'NotApplicable',
}

export const LEARN_MORE_PASSKEY_URL = 'https://developers.google.com/identity/passkeys';
export const LEARN_MORE_AUTHENTICATOR_URL =
  'https://www.keepersecurity.com/blog/2023/07/20/what-are-authenticator-apps-and-how-do-they-work/';

/**
 * Represents the possible verification status values.
 */
export type TStatus =
  | 'Verified' // Verification is successful
  | 'Rejected' // Verification is rejected
  | 'InProgress' // Verification is in progress
  | 'Expired' // Associated verification document has expired
  | 'NotAvailable' // Verification not started
  | 'Matched' // Matched with PEP or Sanction list
  | 'NotMatched'; // Not matched with PEP or Sanction list

export const OtherIdentityDocumentOptions: { [documentType: string]: TOption } = {
  NationalID: { code: 'NationalID', label: 'National ID' },
};

export type TDocument = { label: string; code: keyof typeof DocumentType };

const documentPassport: TDocument = { label: 'Passport', code: DocumentType.Passport };
// UNAPPROVED DRAFT (verifime-adr#816): to be approved with the stakeholder in the running build
const documentImmiCard: TDocument = { label: 'ImmiCard', code: DocumentType.ImmiCard };
const documentDriverLicence: TDocument = {
  label: "Driver's Licence",
  code: DocumentType.DriverLicence,
};
const documentMedicareCard: TDocument = {
  label: 'Medicare Card',
  code: DocumentType.MedicareCard,
};
// UNAPPROVED DRAFT (verifime-adr#816): to be approved with the stakeholder in the running build
const documentPhotoCard: TDocument = { label: 'Photo Card', code: DocumentType.PhotoCard };
const documentOtherGovermentId: TDocument = {
  label: 'Other Government ID',
  code: DocumentType.OtherIdentityDocument,
};

export const DOCUMENT_TYPES: { [code in keyof typeof DocumentType]?: TDocument } = {
  [DocumentType.Passport]: documentPassport,
  [DocumentType.DriverLicence]: documentDriverLicence,
  [DocumentType.MedicareCard]: documentMedicareCard,
  [DocumentType.OtherIdentityDocument]: documentOtherGovermentId,
  [DocumentType.PhotoCard]: documentPhotoCard,
  [DocumentType.ImmiCard]: documentImmiCard,
};

export const COUNTRY_DOCUMENT_TYPES: {
  [CountryCode in TCountryCode]?: TDocument[];
} = {
  [SUPPORTED_COUNTRY_MAP.AU.code]: [documentPassport, documentDriverLicence, documentMedicareCard],
  [SUPPORTED_COUNTRY_MAP.HK.code]: [
    documentPassport,
    documentDriverLicence,
    documentOtherGovermentId,
  ],
  [SUPPORTED_COUNTRY_MAP.NZ.code]: [
    documentPassport,
    documentDriverLicence,
    documentOtherGovermentId,
  ],
  [SUPPORTED_COUNTRY_MAP.SG.code]: [
    documentPassport,
    documentDriverLicence,
    documentOtherGovermentId,
  ],
  [SUPPORTED_COUNTRY_MAP.GB.code]: [
    documentPassport,
    documentDriverLicence,
    documentOtherGovermentId,
  ],
};

export const REGEX_EMAIL =
  /^[a-zA-Z0-9_+.-]{1,64}@([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,63}$/;
